import * as React from "react"
import moment from "moment"
import { CalendarDays, LoaderCircle, Video } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import store from "../learning_strategists/state/store"
import Modal from "../learning_strategists/components/modal"

// Buckets events by their local start day, keeping the API's start-time order.
function groupByDay(calendar_events) {
  const days = []
  const byDate = {}

  calendar_events.forEach((event) => {
    const date = moment(event.start_time).format("YYYY-MM-DD")

    if (!byDate[date]) {
      byDate[date] = { date, events: [] }
      days.push(byDate[date])
    }

    byDate[date].events.push(event)
  })

  return days
}

function dayLabel(date) {
  const day = moment(date, "YYYY-MM-DD")
  const today = moment().startOf("day")

  if (day.isSame(today, "day")) {
    return "Today"
  }

  if (day.isSame(today.clone().add(1, "day"), "day")) {
    return "Tomorrow"
  }

  return day.format("dddd D MMMM")
}

function timeLabel(event) {
  if (event.all_day) {
    return "All day"
  }

  return `${moment(event.start_time).format("HH:mm")} - ${moment(event.end_time).format("HH:mm")}`
}

function CalendarEventsBody({ calendar_events, ui }) {
  if (ui.loading) {
    return (
      <div className="flex justify-center py-12">
        <LoaderCircle className="size-10 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (ui.failed) {
    return <p className="py-12 text-center text-sm text-muted-foreground">Couldn't load your calendar.</p>
  }

  const days = groupByDay(calendar_events)

  if (days.length === 0) {
    return <p className="py-12 text-center text-sm text-muted-foreground">Nothing on your calendar for the next 7 days.</p>
  }

  return days.map((day) => (
    <section key={day.date} className="pt-4 first:pt-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{dayLabel(day.date)}</h3>
      <ul className="mt-2 divide-y overflow-hidden rounded-lg border">
        {day.events.map((event) => (
          <li key={event.id} className="flex items-center gap-3 px-3 py-2 text-sm odd:bg-muted/30">
            <span className="w-28 shrink-0 tabular-nums text-muted-foreground">{timeLabel(event)}</span>
            <span className="min-w-0 flex-1">
              <a
                href={event.html_link}
                target="_blank"
                rel="noreferrer"
                className="block truncate text-foreground no-underline hover:underline"
              >
                {event.title || "(No title)"}
              </a>
              {event.location && <span className="block truncate text-xs text-muted-foreground">{event.location}</span>}
            </span>
            {event.meeting_url && (
              <Button asChild size="xs" variant="outline">
                <a href={event.meeting_url} target="_blank" rel="noreferrer">
                  <Video />
                  Join
                </a>
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  ))
}

export function CalendarEventsModal({ calendar_events, ui, onClose }) {
  return (
    <Modal
      title="My Calendar"
      subtitle="Your Google Calendar for today and the next 7 days."
      size="modal-md"
      onClose={onClose}
    >
      <div className="p-6 pt-2">
        <CalendarEventsBody calendar_events={calendar_events} ui={ui} />
      </div>
    </Modal>
  )
}

// Sits in the rail footer, just above the user menu.
export function NavCalendar({ calendar_events, ui }) {
  const onOpen = () => {
    store.emit("calendar_events:index")
    store.get().ui.calendar_events.set({ open: true })
  }

  const onClose = () => {
    store.get().ui.calendar_events.set({ open: false })
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton tooltip="My Calendar" onClick={onOpen}>
          <CalendarDays />
          <span>My Calendar</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
      {ui.open && <CalendarEventsModal calendar_events={calendar_events} ui={ui} onClose={onClose} />}
    </SidebarMenu>
  )
}
